/**
 * Task #2717 — pure helper for the "past booking" terminal-state line
 * rendered in place of the live ProposalCard / ProposalResponseCard when
 * a trip-adjustment system message belongs to a trip that has since
 * reached a terminal status (completed / cancelled / declined / expired /
 * no_show).
 *
 * Kept as a pure function (no React, no i18n, no router) so we can pin
 * its gating behavior with cheap unit tests:
 *   - returns null when there's a live `active_trip` (live-trip path
 *     must be byte-for-byte unchanged),
 *   - returns null when there's no `last_trip` to link to,
 *   - returns null when the message's payload `trip_id` doesn't match
 *     the conversation's `last_trip.id` — protecting against
 *     mis-labelling a historical proposal that belonged to a *different*
 *     trip between the same parties,
 *   - returns null for legacy payloads that pre-date the `trip_id`
 *     field (graceful fallback to the existing bare italic line),
 *   - and returns a structured object describing the actor / action /
 *     status / link target when all the gates pass.
 *
 * The caller (ConversationPage.tsx) is responsible for resolving i18n
 * strings, building the localized href, and rendering the JSX — this
 * helper deals only in raw keys + IDs so tests don't need a translator
 * or router mounted.
 */

type ProposalRole = "host" | "guest" | null;

type ProposalActionKey =
  | "requested"
  | "accepted"
  | "declined"
  | "withdrew"
  | "responded"
  | "expired";

type PastTripStatusKey =
  | "completed"
  | "cancelled"
  | "declined"
  | "expired"
  | "no_show";

export interface PastProposalLineContext {
  /** Which translation key to use for the action verb. */
  actionKey: ProposalActionKey;
  /** Which translation key to use for the trip-status suffix. */
  statusKey: PastTripStatusKey;
  /** Whether the viewer authored the original system message. */
  viewerIsActor: boolean;
  /** Role fallback for naming the counterpart when sender is unknown. */
  role: ProposalRole;
  /** Public id of the past trip — used to build the booking href. */
  pastTripPublicId: string;
  /** Owner-vs-renter split for booking href routing. */
  pastTripIsOwner: boolean;
}

interface MessageLike {
  is_system?: boolean | null;
  system_type?: string | null;
  system_payload?: unknown;
  sender_id?: string | number | null;
}

interface ConversationLike {
  active_trip?: { id?: number } | null;
  last_trip?: {
    id: number;
    public_id: string;
    status: string;
    isOwner: boolean;
  } | null;
}

const PROPOSAL_FAMILY = new Set([
  "trip_proposal",
  "trip_proposal_response",
  "trip_proposal_expired",
]);

// Terminal trip statuses — a booking in any of these holds no live calendar
// dates and the booking engine permits a fresh booking between the same pair.
// Exported so trip-scoped surfaces (e.g. the conversation reservation panel's
// "Build a trip" CTA) can share one definition instead of hand-rolling it.
export const TERMINAL_TRIP_STATUSES: ReadonlySet<PastTripStatusKey> = new Set([
  "completed",
  "cancelled",
  "declined",
  "expired",
  "no_show",
]);

const TERMINAL_STATUSES = TERMINAL_TRIP_STATUSES;

/**
 * Returns the rendering context for a past-trip terminal-state line, or
 * `null` if the caller should fall back to its existing rendering path.
 */
export function selectPastProposalLine(
  msg: MessageLike,
  conv: ConversationLike,
  viewerId: string | number | null | undefined,
): PastProposalLineContext | null {
  // Gate (a): a live trip means the live-trip rendering path owns this
  // message — leave it alone.
  if (conv.active_trip && conv.active_trip.id != null) return null;

  // Gate (b): we need a past trip to link to.
  const pastTrip = conv.last_trip;
  if (!pastTrip) return null;
  if (!TERMINAL_STATUSES.has(pastTrip.status as PastTripStatusKey)) return null;

  // Gate (c): only proposal-family system messages get the variant.
  if (!msg.is_system) return null;
  if (!msg.system_type || !PROPOSAL_FAMILY.has(msg.system_type)) return null;

  // Gate (d): the payload should reference the same trip we're about
  // to link to. New proposal-family payloads (post Task #2717) carry
  // `correlation_trip_id` (we can't reuse `trip_id` because
  // `sendBookingSystemMessage` dedupes by it — see the server-side
  // comment in routes/bookings.ts).
  //
  // Legacy rows emitted before this task have NO correlation field on
  // the payload, which is exactly the orphaned-italic case this task
  // is meant to fix. For those rows we fall back to the conversation's
  // single `last_trip` — safe here because:
  //   (i) the conversation already has no `active_trip` (gate a),
  //  (ii) the server only returns *one* `last_trip` per conversation
  //       (the most recent terminal trip between the same two users
  //        on the same car), so there is no ambiguity about which
  //        past trip the historical proposal belonged to.
  // When `correlation_trip_id` IS present and points elsewhere, we
  // still bail out — that guards against a future, unrelated
  // historical proposal sneaking onto the past trip's line.
  const payload = (msg.system_payload && typeof msg.system_payload === "object")
    ? (msg.system_payload as { correlation_trip_id?: unknown; role?: unknown; suggested_by_role?: unknown; action?: unknown })
    : {};
  if (typeof payload.correlation_trip_id === "number" && payload.correlation_trip_id !== pastTrip.id) {
    return null;
  }

  // Actor: prefer the trusted sender_id (matched against the viewer's
  // user id) over the optional `role` field, which is only reliably
  // present on `trip_proposal` payloads.
  const senderId = msg.sender_id != null ? String(msg.sender_id) : null;
  const viewerIdStr = viewerId != null ? String(viewerId) : null;
  let viewerIsActor = false;
  if (senderId != null && viewerIdStr != null) {
    viewerIsActor = senderId === viewerIdStr;
  } else {
    const role = typeof payload.suggested_by_role === "string"
      ? payload.suggested_by_role
      : typeof payload.role === "string" ? payload.role : null;
    if (role === "host") viewerIsActor = pastTrip.isOwner;
    else if (role === "guest") viewerIsActor = !pastTrip.isOwner;
  }

  // Action — viewer-facing verb keyed off the system_type + (for
  // responses) the inner `action` discriminant.
  let actionKey: ProposalActionKey;
  if (msg.system_type === "trip_proposal_response") {
    const action = typeof payload.action === "string" ? payload.action : null;
    if (action === "accepted") actionKey = "accepted";
    else if (action === "declined") actionKey = "declined";
    else if (action === "cancelled" || action === "withdrawn") actionKey = "withdrew";
    else actionKey = "responded";
  } else if (msg.system_type === "trip_proposal_expired") {
    actionKey = "expired";
  } else {
    actionKey = "requested";
  }

  const roleFallback: ProposalRole = (typeof payload.suggested_by_role === "string"
    ? payload.suggested_by_role
    : typeof payload.role === "string" ? payload.role : null) as ProposalRole;

  return {
    actionKey,
    statusKey: pastTrip.status as PastTripStatusKey,
    viewerIsActor,
    role: roleFallback === "host" || roleFallback === "guest" ? roleFallback : null,
    pastTripPublicId: pastTrip.public_id,
    pastTripIsOwner: pastTrip.isOwner,
  };
}
